import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { getApiBaseUrl } from '../utils/Config';
import logo from '../images/stacktalent-708X214.jpg';

const Login = () => {
    const navigate = useNavigate();
    const baseUrl = getApiBaseUrl();
    const [mode, setMode] = useState('signin');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    const handleSignIn = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });

        if (signInError) {
            setError(signInError.message);
            setLoading(false);
            return;
        }

        try {
            const res = await fetch(
                `${baseUrl}/api/Authentication/DoesCandidateExist?b2cObjectId=${data.user.id}`,
                { headers: { Authorization: `Bearer ${data.session.access_token}` } }
            );
            const result = await res.json();
            const returnTo = sessionStorage.getItem('postLoginRedirect') || '/candidate/profile';
            sessionStorage.removeItem('postLoginRedirect');
            navigate(result.success && result.result ? returnTo : '/candidate/additional-info');
        } catch {
            navigate('/candidate/profile');
        }

        setLoading(false);
    };

    const handleSignUp = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        const { data, error: signUpError } = await supabase.auth.signUp({
            email,
            password,
            options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
        });

        if (signUpError) {
            setError(signUpError.message);
            setLoading(false);
            return;
        }

        // If email confirmation is disabled (auto-confirm), session is returned immediately
        if (data.session) {
            try {
                const res = await fetch(
                    `${baseUrl}/api/Authentication/DoesCandidateExist?b2cObjectId=${data.user.id}`,
                    { headers: { Authorization: `Bearer ${data.session.access_token}` } }
                );
                const result = await res.json();
                navigate(result.success && result.result ? '/candidate/profile' : '/candidate/additional-info');
            } catch {
                navigate('/candidate/additional-info');
            }
        } else {
            setMessage('Check your email for a confirmation link to complete registration.');
        }

        setLoading(false);
    };

    const switchMode = (newMode) => {
        setMode(newMode);
        setError(null);
        setMessage(null);
    };

    return (
        <div
            className="min-vh-100 d-flex align-items-center justify-content-center"
            style={{ background: 'var(--tu-gradient-nav)' }}
        >
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-12 col-sm-10 col-md-7 col-lg-5 col-xl-4">
                        <div className="card border-0 p-4" style={{ borderRadius: '16px', boxShadow: 'var(--tu-shadow-hover)' }}>

                            <div className="text-center mb-4">
                                <img src={logo} alt="Stacktalent" style={{ height: '36px', objectFit: 'contain' }} />
                            </div>

                            {/* Mode toggle */}
                            <div className="d-flex mb-4" style={{ borderBottom: '1px solid var(--tu-border)' }}>
                                <button
                                    type="button"
                                    onClick={() => switchMode('signin')}
                                    className="btn btn-link text-decoration-none fw-semibold pb-2 me-3 px-0"
                                    style={{
                                        borderRadius: 0,
                                        color: mode === 'signin' ? 'var(--tu-navy-900)' : 'var(--tu-text-muted)',
                                        borderBottom: mode === 'signin' ? '2px solid var(--tu-cyan-500)' : '2px solid transparent',
                                    }}
                                >
                                    Sign In
                                </button>
                                <button
                                    type="button"
                                    onClick={() => switchMode('signup')}
                                    className="btn btn-link text-decoration-none fw-semibold pb-2 px-0"
                                    style={{
                                        borderRadius: 0,
                                        color: mode === 'signup' ? 'var(--tu-navy-900)' : 'var(--tu-text-muted)',
                                        borderBottom: mode === 'signup' ? '2px solid var(--tu-cyan-500)' : '2px solid transparent',
                                    }}
                                >
                                    Create Account
                                </button>
                            </div>

                            {error && (
                                <div className="alert alert-danger py-2 small mb-3">{error}</div>
                            )}
                            {message && (
                                <div className="alert alert-success py-2 small mb-3">{message}</div>
                            )}

                            <form onSubmit={mode === 'signin' ? handleSignIn : handleSignUp}>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold small" style={{ color: 'var(--tu-text-primary)' }}>
                                        Email address
                                    </label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        autoComplete="email"
                                    />
                                </div>

                                <div className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <label className="form-label fw-semibold small mb-0" style={{ color: 'var(--tu-text-primary)' }}>
                                            Password
                                        </label>
                                        {mode === 'signin' && (
                                            <Link
                                                to="/auth/forgot-password"
                                                className="small text-decoration-none"
                                                style={{ color: 'var(--tu-cyan-600)' }}
                                            >
                                                Forgot password?
                                            </Link>
                                        )}
                                    </div>
                                    <input
                                        type="password"
                                        className="form-control"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                                        minLength={mode === 'signup' ? 8 : undefined}
                                    />
                                    {mode === 'signup' && (
                                        <div className="form-text">Minimum 8 characters</div>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    className="btn w-100 fw-semibold py-2"
                                    style={{
                                        background: 'var(--tu-navy-900)',
                                        color: 'var(--tu-text-on-dark)',
                                        borderRadius: '8px',
                                    }}
                                    disabled={loading}
                                >
                                    {loading && (
                                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                                    )}
                                    {mode === 'signin' ? 'Sign In' : 'Create Account'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
